import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Keeps an open tab on the current version of the app.
 *
 * Every few minutes it fetches the app shell and compares the bundle it names
 * with the one this tab is running. When a newer version is out, the tab
 * reloads on the next page change (a change of path, never of query string),
 * so nobody loses what they are typing in a form.
 */
const CHECK_EVERY_MS = 5 * 60 * 1000;

function bundleOf(html: string): string | null {
  const m = html.match(/\/assets\/index-[^"']+\.js/);
  return m ? m[0] : null;
}

function currentBundle(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]');
  if (!el) return null;
  try {
    return new URL(el.src).pathname;
  } catch {
    return null;
  }
}

export default function VersionWatcher() {
  const { pathname } = useLocation();
  const stale = useRef(false);
  const lastPath = useRef(pathname);

  useEffect(() => {
    const mine = currentBundle();
    if (!mine) return; // dev server: nothing to compare
    let stopped = false;
    const check = async () => {
      try {
        const res = await fetch("/", { cache: "no-store" });
        if (!res.ok) return;
        const latest = bundleOf(await res.text());
        if (!stopped && latest && latest !== mine) stale.current = true;
      } catch {
        /* offline: try again later */
      }
    };
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    if (pathname !== lastPath.current) {
      lastPath.current = pathname;
      if (stale.current) window.location.reload();
    }
  }, [pathname]);

  return null;
}
